import Breadcrumbs from '../components/Breadcrumbs.jsx';
import {
  BookIcon,
  BulbIcon,
  CheckIcon,
  CollegeIcon,
  DocumentIcon,
  ExamIcon,
  FolderIcon,
  RefreshIcon,
  ShieldIcon,
  UsersIcon,
} from '../icons/index.js';

const quickAccessCards = [
  {
    title: 'Explore Colleges',
    description:
      'Browse Engineering, Medical, AYUSH, Polytechnic, and Paramedical colleges in Bihar with cutoffs, seat matrix, fees, and key details.',
    tone: 'violet',
    path: '/colleges',
    icon: CollegeIcon,
  },
  {
    title: 'Cutoff & Seat Matrix Hub',
    description:
      'Compare opening and closing ranks, category-wise cutoffs, and seat availability across Bihar exams from one hub.',
    tone: 'emerald',
    path: '/cutoff-trends',
    icon: RefreshIcon,
  },
  {
    title: 'Admission & Counselling Guide',
    description:
      'Step-by-step guidance on counselling rounds, document requirements, and smart strategies to help you secure your seat with confidence.',
    tone: 'blue',
    path: '/admission-guide',
    icon: DocumentIcon,
  },
];

const trustFeatures = [
  {
    title: 'Trusted & Verified',
    description:
      'All cutoff data, college info, and study materials are carefully verified from reliable official sources.',
    icon: CheckIcon,
    tone: 'blue',
  },
  {
    title: 'Neatly Organized',
    description: 'Resources sorted by stream, branch, and semester so you can find what you need in seconds.',
    icon: FolderIcon,
    tone: 'emerald',
  },
  {
    title: 'Always Up to Date',
    description:
      "Cutoffs, seat matrices, syllabus, and notices are updated regularly so you're never working with outdated info.",
    icon: RefreshIcon,
    tone: 'violet',
  },
  {
    title: 'Built for Bihar Students',
    description:
      'Covers Engineering, Medical, AYUSH, Polytechnic and Paramedical in one platform built to reduce confusion.',
    icon: UsersIcon,
    tone: 'amber',
  },
];

const toneStyles = {
  blue: {
    ring: 'border-blue-100 bg-blue-50 text-blue-700',
    soft: 'from-blue-50 to-white',
    icon: 'bg-blue-600 text-white',
    badge: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
    arrow: 'text-blue-600',
  },
  emerald: {
    ring: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    soft: 'from-emerald-50 to-white',
    icon: 'bg-emerald-600 text-white',
    badge: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
    arrow: 'text-emerald-600',
  },
  violet: {
    ring: 'border-violet-100 bg-violet-50 text-violet-700',
    soft: 'from-violet-50 to-white',
    icon: 'bg-violet-600 text-white',
    badge: 'bg-violet-50 text-violet-700',
    dot: 'bg-violet-500',
    arrow: 'text-violet-600',
  },
  amber: {
    ring: 'border-amber-100 bg-amber-50 text-amber-700',
    soft: 'from-amber-50 to-white',
    icon: 'bg-amber-500 text-white',
    badge: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
    arrow: 'text-amber-600',
  },
  rose: {
    ring: 'border-rose-100 bg-rose-50 text-rose-700',
    soft: 'from-rose-50 to-white',
    icon: 'bg-rose-500 text-white',
    badge: 'bg-rose-50 text-rose-700',
    dot: 'bg-rose-500',
    arrow: 'text-rose-600',
  },
  slate: {
    ring: 'border-slate-200 bg-slate-50 text-slate-700',
    soft: 'from-slate-50 to-white',
    icon: 'bg-slate-700 text-white',
    badge: 'bg-slate-100 text-slate-700',
    dot: 'bg-slate-400',
    arrow: 'text-slate-600',
  },
};

const HomePage = ({ onNavigate }) => {
  return (
    <main className="page-shell">
      <Breadcrumbs items={[{ label: 'Home' }]} onNavigate={onNavigate} />
      <section
        className="relative bg-white px-4 py-5 sm:px-7 sm:py-7 lg:px-9 lg:py-8"
      >
        <div className="relative grid gap-5 sm:gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.08em] text-blue-700 shadow-sm sm:px-4 sm:text-xs">
              <BulbIcon className="h-4 w-4" />
              Your Journey, Our Guidance
            </span>

            <h1 className="mt-4 max-w-3xl text-[28px] font-black leading-[1.12] tracking-normal text-slate-950 sm:text-4xl xl:text-5xl">
              Bihar&apos;s most accurate <span className="whitespace-nowrap">Platform for</span>
              <span className="block text-blue-600">College Prediction,</span>
              <span className="block">Cutoff Analysis &amp; Student Resources.</span>
            </h1>

            <p className="mt-3 max-w-2xl text-[15px] leading-6 text-slate-600 sm:mt-4 sm:text-base sm:leading-7">
              Whether you&apos;re predicting your college, navigating admissions, or looking for
              study materials, CampusGuide has everything Bihar students need, all in one place.
            </p>

            <div className="mt-5 grid max-w-[640px] gap-3 sm:mt-6 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => onNavigate('/predictor')}
                className="relative z-10 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-2xl border border-violet-500 bg-violet-600 px-5 py-3 text-[15px] font-black text-white shadow-[0_14px_28px_rgba(124,58,237,0.28)] transition duration-200 hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-[0_18px_34px_rgba(124,58,237,0.32)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-500/20"
              >
                <ExamIcon className="h-5 w-5 text-white" />
                <span>Predict My College</span>
                <span aria-hidden="true">&rarr;</span>
              </button>
              <button
                type="button"
                onClick={() => onNavigate('/resources')}
                className="relative z-10 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-[15px] font-black text-slate-800 shadow-[0_10px_22px_rgba(15,23,42,0.07)] transition duration-200 hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-700 hover:shadow-[0_16px_30px_rgba(15,23,42,0.10)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15"
              >
                <BookIcon className="h-5 w-5" />
                <span>Explore Resources</span>
                <span aria-hidden="true">&rarr;</span>
              </button>
            </div>
          </div>

          <div className="relative flex items-center justify-center px-2 py-2 lg:justify-end">
            <img
              src="/home-hero.webp"
              alt="Engineering journey illustration"
              width="720"
              height="450"
              fetchPriority="high"
              decoding="async"
              className="relative z-10 h-auto w-full max-w-[420px] object-contain object-center sm:max-w-[560px] xl:max-w-[640px]"
            />
          </div>
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {trustFeatures.map((feature) => {
          const Icon = feature.icon;
          const tone = toneStyles[feature.tone];

          return (
            <article
              key={feature.title}
              className="rounded-lg border border-slate-200/90 bg-white px-3.5 py-3.5 shadow-[0_6px_16px_rgba(15,23,42,0.04)] sm:px-4 sm:py-4"
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone.ring}`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-[13px] font-black leading-5 text-slate-950">{feature.title}</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-600">{feature.description}</p>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {quickAccessCards.map((card) => {
          const Icon = card.icon;
          const tone = toneStyles[card.tone];

          return (
            <button
              key={card.title}
              type="button"
              onClick={() => onNavigate(card.path)}
              className={`section-card group flex items-start gap-4 bg-gradient-to-br ${tone.soft} p-5 text-left transition duration-200 hover:-translate-y-1 hover:shadow-[0_18px_34px_rgba(15,23,42,0.09)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 sm:p-6`}
            >
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-sm sm:h-16 sm:w-16 sm:rounded-full ${tone.icon}`}>
                <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h3 className={`text-lg font-black leading-6 sm:text-xl ${tone.arrow}`}>{card.title}</h3>
                  <span className={`text-2xl font-black transition group-hover:translate-x-1 ${tone.arrow}`}>
                    &rarr;
                  </span>
                </div>
                <p className="mt-2 text-sm leading-7 text-slate-600">{card.description}</p>
              </div>
            </button>
          );
        })}
      </section>

    </main>
  );
};

export default HomePage;
